import React, { useContext, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { Context } from "../../../utils/context";

// Form state keeps the selected answers per question under
// question_answers.q<question_id> (prefixed so react-hook-form doesn't treat ids as array indexes)
export const questionFieldName = (questionId) => `question_answers.q${questionId}`;

// Flattens every question's selected answers into one id list for the API
export const getQuestionAnswerIds = (questionAnswers) =>
  Object.values(questionAnswers || {})
    .flat()
    .map((item) => item?.value)
    .filter(Boolean);

// Converts the product detail response ("questions" grouped per question) into form state
export const toQuestionAnswersForm = (questions) =>
  (questions || []).reduce((acc, item) => {
    acc[`q${item.question_id}`] = item.answers || [];
    return acc;
  }, {});

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#007185" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 1px #007185" : "none",
    "&:hover": { borderColor: "#007185" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "4px 8px",
    gap: "4px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e0f1f4",
    borderRadius: "14px",
    padding: "1px 4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#00515f",
    fontWeight: 500,
    fontSize: "13px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#00515f",
    borderRadius: "50%",
    ":hover": {
      backgroundColor: "#d33",
      color: "#fff",
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e0f1f4"
      : state.isFocused
        ? "#f1f9fa"
        : "#fff",
    color: "#222",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    borderRadius: "8px",
  }),
};

const ProductQuestions = ({ control }) => {
  const { getData, IMG_URL } = useContext(Context);
  const [questions, setQuestions] = useState([]);

  // Shows the answer's image (if any) next to its text, in the menu and in the chips
  const formatOptionLabel = (option) => (
    <div className="d-flex align-items-center gap-2">
      {option.image && (
        <img
          src={IMG_URL + option.image}
          alt={option.label}
          style={{
            width: "22px",
            height: "22px",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        />
      )}
      <span>{option.label}</span>
    </div>
  );

  const getAllQuestions = async () => {
    const response = await getData(`/common/masters/all-questions`);
    if (response?.success) {
      setQuestions(response?.data || []);
    }
  };

  useEffect(() => {
    getAllQuestions();
  }, []);

  if (!questions.length) return null;

  return (
    <div className="col-12 mb-2">
      <Form.Label className="fw-bold">Product Questions</Form.Label>
      <div className="row">
        {questions.map((question) => (
          <div
            key={question.id}
            className="col-xxl-4 col-xl-4 col-lg-6 col-md-6 col-12 mb-2"
          >
            <Form.Label>{question.question}</Form.Label>
            <Controller
              name={questionFieldName(question.id)}
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={true}
                  options={(question.answers || []).map((answer) => ({
                    value: answer.id,
                    name: "question_answer_ids",
                    label: answer.answer,
                    image: answer.image,
                  }))}
                  formatOptionLabel={formatOptionLabel}
                  menuPlacement="auto"
                  styles={selectStyles}
                  onChange={(selectedValues) => {
                    onChange(selectedValues || []);
                  }}
                  onBlur={onBlur}
                  value={value || []}
                  ref={ref}
                  placeholder="Select Answers"
                />
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductQuestions;
